/**
 * The only place that talks to the network.
 *
 * Attaches the bearer token and acts on the backend's verdict:
 *   401 -> the session is dead, back to SEP (once, not once per request)
 *   403 -> valid token, not admitted; the app shows a terminal screen
 *   503 -> SEP is unreachable; an outage, never a sign-out
 */
import { currentToken, redirectToLogin } from '@/auth/session';
import { currentEnvironment } from '@/environment/store';
import type { ApiErrorBody } from '@/types/api';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, detail: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  /** SEP itself is down. Distinct from a rejection — do not sign the user out. */
  get isOutage(): boolean {
    return this.status === 503;
  }

  /** Someone else changed this row first (doc 05). */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Attach the selected environment to every request (doc 18).
 *
 * Every call, not only writes: a read that forgot it would silently answer for
 * production while the switcher said development, which is the failure this
 * whole mechanism exists to prevent. Writes would at least fail loudly — the
 * backend rejects them without `env` — so it is the reads that need the care.
 *
 * A path that already names one keeps it, so a caller with a specific target
 * (the copy-from-production preview) is not overruled.
 */
function withEnvironment(path: string): string {
  const [base, existing] = path.split('?');
  const params = new URLSearchParams(existing);
  if (!params.has('env')) params.set('env', currentEnvironment());
  return `${base}?${params.toString()}`;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const headers: Record<string, string> = { Accept: 'application/json' };

  const token = currentToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`/api/v1${withEnvironment(path)}`, {
      method,
      headers,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Keine Verbindung zum Server.');
  }

  if (response.status === 401) {
    redirectToLogin();
    throw new ApiError(401, 'unauthenticated', 'Sitzung abgelaufen.');
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const error = (typeof payload === 'object' && payload !== null
      ? (payload as Partial<ApiErrorBody>)
      : {}) as Partial<ApiErrorBody>;
    throw new ApiError(
      response.status,
      error.error ?? 'error',
      error.detail ?? `Fehler ${response.status}`,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
